import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { ChevronRight } from "lucide-react";
import { LogoutButton } from "../components/LogoutButton";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { pushSupported, enablePush, disablePush } from "../lib/push";
import { PasswordField } from "../components/PasswordField";
import { isStrongPassword, PASSWORD_REQUIREMENTS_MESSAGE, PASSWORD_MIN_LENGTH } from "../lib/password";
import { JobseekerDiscoveryControl } from "../components/JobseekerDiscoveryControl";
import { LANGUAGES, useI18n } from "../lib/i18n";
import { Card, CardContent } from "../components/beui/SurfaceCard";
import { Input } from "../components/beui/Input";
import { Button } from "../components/beui/Button";
import { StatefulButton } from "../components/beui/StatefulButton";
import { Tabs, TabsList, TabsTrigger } from "../components/beui/Tabs";
import { Switch } from "../components/beui/Switch";
import { InstalledAppVersion } from "../components/InstalledAppVersion";
import { isNativeApp } from "../lib/platform";
import { AccountStanding } from "./settings/AccountStanding";
import { SettingsDataRights } from "./settings/SettingsDataRights";
import "./Settings.mobile.css";
import "./SettingsA.css";

export default function Settings() {
  const { t, lang, setLang } = useI18n();
  const { user, loading, logout } = useAuth();
  const [isMobileViewport, setIsMobileViewport] = useState(() => window.matchMedia("(max-width: 639px)").matches);
  useEffect(() => {
    const media = window.matchMedia("(max-width: 639px)");
    const updateViewport = () => setIsMobileViewport(media.matches);
    media.addEventListener("change", updateViewport);
    return () => media.removeEventListener("change", updateViewport);
  }, []);
  const [, navigate] = useLocation();
  const [logoutError, setLogoutError] = useState("");
  async function signOut() {
    setLogoutError("");
    try { await logout(); navigate("/"); }
    catch (failure: unknown) { setLogoutError("Could not sign out. Please try again."); throw failure; }
  }
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Increment 24: change email
  const [newEmail, setNewEmail] = useState("");
  const [emailPw, setEmailPw] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailErr, setEmailErr] = useState<string | null>(null);
  const [emailPending, setEmailPending] = useState<string | null>(null);
  const [emailConfirmUrl, setEmailConfirmUrl] = useState<string | null>(null);

  // Increment 35: email notification preference
  const [emailNotif, setEmailNotif] = useState(true);
  const [emailNotifBusy, setEmailNotifBusy] = useState(false);

  // Increment 36: browser push
  const [pushInfo, setPushInfo] = useState<{ configured: boolean; count: number } | null>(null);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMsg, setPushMsg] = useState<string | null>(null);

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);
  useEffect(() => { if (user) api.getEmailPrefs().then((r) => setEmailNotif(r?.emailNotifications ?? true)).catch(() => {}); }, [user]);
  useEffect(() => {
    if (!user || !pushSupported()) return;
    api.pushStatus().then((r) => setPushInfo(r)).catch(() => {});
    navigator.serviceWorker?.getRegistration().then((reg) => reg?.pushManager.getSubscription()).then((s) => setPushOn(!!s)).catch(() => {});
  }, [user]);

  async function changeEmailNotifications(enabled: boolean) {
    setEmailNotif(enabled);
    setEmailNotifBusy(true);
    try { await api.setEmailPrefs(enabled); }
    catch { setEmailNotif(!enabled); }
    finally { setEmailNotifBusy(false); }
  }

  async function togglePush() {
    setPushBusy(true); setPushMsg(null);
    try {
      if (pushOn) { await disablePush(); setPushOn(false); setPushMsg("Push turned off for this device."); }
      else {
        const r = await enablePush();
        if (r.ok) { setPushOn(true); setPushMsg("Push enabled on this device."); }
        else setPushMsg(r.reason === "denied" ? "Permission was blocked in your browser." : r.reason === "not_configured" ? "Push isn't configured on the server yet." : "Couldn't enable push on this browser.");
      }
    } catch (e: any) { setPushMsg(e?.message || "Push action failed."); }
    finally { setPushBusy(false); }
  }
  async function testPush() {
    setPushBusy(true); setPushMsg(null);
    try { const r = await api.pushTest(); setPushMsg(`Test push sent to ${r?.delivered ?? 0} device(s).`); }
    catch (e: any) { setPushMsg(e?.message || "Could not send test."); }
    finally { setPushBusy(false); }
  }

  if (loading || !user) return <div className="container-x py-20 text-center text-mut">Loading…</div>;

  const canEditProfile = user.capabilities.some((capability) => capability === "find_work" || capability === "offer_service");

  async function changeEmail(e: React.FormEvent) {
    e.preventDefault(); setEmailErr(null); setEmailPending(null); setEmailConfirmUrl(null);
    setEmailBusy(true);
    try {
      const r = await api.requestEmailChange(newEmail.trim(), emailPw);
      setEmailPending(r?.pendingEmail || newEmail.trim());
      if (r?.confirmUrl) setEmailConfirmUrl(r.confirmUrl.replace(/^https?:\/\/[^/]+/, ""));
      setNewEmail(""); setEmailPw("");
    } catch (e: any) { setEmailErr(e?.message || "Could not start the email change."); }
    finally { setEmailBusy(false); }
  }

  async function changePassword(e: React.FormEvent) {
    e.preventDefault(); setErr(null); setDone(false);
    if (!isStrongPassword(next)) { setErr(PASSWORD_REQUIREMENTS_MESSAGE); return; }
    if (next !== confirm) { setErr("New passwords don't match."); return; }
    setBusy(true);
    try { await api.changePassword(cur, next); setDone(true); setCur(""); setNext(""); setConfirm(""); }
    catch (e: any) { setErr(e?.message || "Could not change your password."); }
    finally { setBusy(false); }
  }

  const panel=(key:string,title:string,children:React.ReactNode)=><Card size="compact" className="settings-a-card" data-settings-panel={key}><CardContent><h2>{title}</h2>{children}</CardContent></Card>;
  return <div className="settings-a container-x py-6">
    <h1 className="text-2xl font-extrabold text-navy">{t("settings.title")}</h1>
    <div className="settings-a-sections">
      {panel('visibility',t('settings.profile'),<><div className="settings-a-row"><p>Name, work history, skills and services</p><Link href={canEditProfile?'/worker-profile?field=name':'/settings/profile'} className="btn-red min-h-11">{t('settings.editProfile')}<ChevronRight className="h-4 w-4"/></Link></div><JobseekerDiscoveryControl userId={user.id} beui /></>)}
      <div className="settings-a-standing"><AccountStanding mobileSettings={false}/></div>
      {panel('email',t('settings.changeEmail'),emailPending?<div className="settings-a-result" role="status"><p>Confirmation sent to <b>{emailPending}</b>. It becomes your sign-in email once you confirm from that address.</p>{emailConfirmUrl&&<p>Email delivery isn't live on UAT yet — <Link href={emailConfirmUrl} className="text-brand-red underline">confirm the change now</Link>.</p>}</div>:<form onSubmit={changeEmail} className="settings-a-form"><p className="text-sm text-mut">{t('settings.current')}: <b className="text-navy">{user.email}</b></p><div className="settings-a-fields"><Input id="settings-new-email" label={t('settings.newEmail')} type="email" required value={newEmail} onChange={setNewEmail} autoComplete="email"/><div><label className="label" htmlFor="settings-email-password">{t('settings.currentPassword')}</label><PasswordField id="settings-email-password" required value={emailPw} onChange={setEmailPw} autoComplete="current-password"/></div></div>{emailErr&&<p role="alert" className="text-sm text-brand-red">{emailErr}</p>}<StatefulButton type="submit" variant="outline" className="min-h-11" state={emailBusy?'loading':'idle'} disabled={emailBusy}>{t('settings.sendConfirmation')}</StatefulButton></form>)}
      {panel('password',t('settings.changePassword'),<form onSubmit={changePassword} className="settings-a-form"><div><label className="label" htmlFor="settings-current-password">{t('settings.currentPassword')}</label><PasswordField id="settings-current-password" value={cur} onChange={setCur} autoComplete="current-password"/></div><div className="settings-a-fields"><div><label className="label" htmlFor="settings-new-password">{t('settings.newPassword')}</label><PasswordField id="settings-new-password" minLength={PASSWORD_MIN_LENGTH} value={next} onChange={setNext} autoComplete="new-password" placeholder={t('settings.createPassword')} showRequirements/></div><div><label className="label" htmlFor="settings-confirm-password">{t('settings.confirmNewPassword')}</label><PasswordField id="settings-confirm-password" minLength={PASSWORD_MIN_LENGTH} value={confirm} onChange={setConfirm} autoComplete="new-password" placeholder={t('settings.reenterPassword')}/></div></div>{err&&<p role="alert" className="text-sm text-brand-red">{err}</p>}{done&&<p role="status" className="text-sm text-brand-green">{t('settings.passwordUpdated')}</p>}<StatefulButton type="submit" className="settings-a-save min-h-11" state={busy?'loading':'idle'} disabled={busy}>{t('settings.updatePassword')}</StatefulButton></form>)}
      {panel('notifications',t('settings.notifications'),<><div className="settings-a-row"><div><h3>{t('settings.emailNotificationsTitle')}</h3><p>{t('settings.emailEvents')}</p></div><Switch ariaLabel={t('settings.emailNotificationsTitle')} checked={emailNotif} disabled={emailNotifBusy} onCheckedChange={value=>void changeEmailNotifications(value)}/></div><div className="settings-a-row"><div><h3>{t('settings.browserPushTitle')}</h3><p>{!pushSupported()?t('settings.pushUnsupported'):!pushInfo?t('settings.pushLoading'):!pushInfo.configured?t('settings.pushUnavailable'):t('settings.deviceOnly')}</p></div><Switch ariaLabel={t('settings.browserPushTitle')} checked={pushOn} disabled={pushBusy||!pushSupported()||!pushInfo?.configured} onCheckedChange={()=>void togglePush()}/></div>{pushOn&&pushInfo?.configured&&<Button variant="outline" disabled={pushBusy} onClick={()=>void testPush()}>{t('settings.sendTest')}</Button>}{pushMsg&&<p role="status" className="text-sm text-mut">{pushMsg}</p>}</>)}
      {panel('preferences',t('settings.language'),<Tabs value={lang} variant="segment" onValueChange={value=>{const language=LANGUAGES.find(option=>option.code===value);if(language)setLang(language.code)}}><TabsList aria-label={t('settings.language')}>{LANGUAGES.map(language=><TabsTrigger key={language.code} value={language.code}>{language.label}</TabsTrigger>)}</TabsList></Tabs>)}
      <SettingsDataRights user={user} isMobileViewport={isMobileViewport}/>
    </div>
    <div className="settings-a-footer"><Link href="/support">Help center</Link><Link href="/legal">Legal & privacy</Link><Link href="/changelog">Changelog</Link><LogoutButton variant="ghost" className="min-h-11" onLogout={signOut}>Log out</LogoutButton></div>
    {logoutError&&<p role="alert" className="text-sm text-brand-red">{logoutError}</p>}
    {(isMobileViewport || isNativeApp())&&<InstalledAppVersion/>}
  </div>;
}
